<template>
  <div>
    <AppNavbar />
    <div class="privacy-page-root">
      <div class="hero-full-bleed-row">
        <section class="privacy-hero">
          <div class="hero-content reveal">
            <p class="eyebrow">Compliance</p>
            <h1>Privacy Policy</h1>
            <p class="hero-description">
              Learn how IndieKonnect collects, uses, stores, and protects your
              personal information.
            </p>
          </div>
        </section>
      </div>

      <div class="content-full-bleed-row">
        <section class="policy-section">
          <div class="policy-wrapper">
            <div class="policy-block reveal">
              <h3>1. Introduction and Scope:</h3>
              <p>
                <strong>IndieKonnect</strong> ("Company," "we," "us," or "our")
                is a direct selling company engaged in the marketing and
                distribution of products through a network of independent direct
                sellers, distributors, and customers across India. We are
                committed to conducting our direct selling operations with
                integrity, transparency, and full compliance with applicable
                Indian law, including the Consumer Protection (Direct Selling)
                Rules, 2021 framed under the Consumer Protection Act, 2019, and
                the Information Technology (Reasonable Security Practices and
                Procedures and Sensitive Personal Data or Information) Rules,
                2011 ("SPDI Rules") issued under the Information Technology Act,
                2000.
              </p>
              <p>
                This Privacy Policy ("Policy") describes how IndieKonnect
                collects, uses, stores, discloses, and protects personal data
                obtained from:
              </p>
              <ul>
                <li>
                  Website visitors ("Visitors") who browse www.indiekonnect.com
                  or any related digital platforms operated by us ("Website");
                </li>
                <li>
                  Independent Direct Sellers ("Direct Sellers") who register
                  with and operate an independent direct selling business
                  through IndieKonnect; and
                </li>
                <li>
                  Customers who purchase IndieKonnect products or services,
                  whether online or through our direct selling network.
                </li>
              </ul>
              <p>
                By accessing our Website, registering as a Direct Seller, or
                purchasing our products, you acknowledge that you have read,
                understood, and consent to the practices described in this
                Policy. If you do not agree to this Policy, please do not use
                our Website or services.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>2. Our Direct Selling Business Model:</h3>
              <p>
                <strong>IndieKonnect</strong> operates as a direct selling
                company. In our model, products are marketed and sold directly
                to consumers by an independent network of Direct Sellers,
                without reliance on traditional retail channels. Direct Sellers
                may also build and mentor their own networks of other Direct
                Sellers, earning performance-based compensation in accordance
                with IndieKonnect's Sales and Marketing Plan and applicable
                regulations.
              </p>
              <p>
                Our direct selling model is governed by the Consumer Protection
                (Direct Selling) Rules, 2021, and we are committed to full
                compliance with the legal requirements applicable to legitimate
                direct selling companies operating in India. The processing of
                personal data described in this Policy is integral to the
                operation of our direct selling business, including the
                registration and management of Direct Sellers, the processing of
                orders, and the administration of the IndieKonnect Sales and
                Marketing Plan.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>3. Information We Collect:</h3>

              <h4>3.1 Information Collected from Website Visitors</h4>
              <p>
                When you visit our website without registering or logging in, we
                may collect the following categories of information:
              </p>
              <ul>
                <li>
                  Technical and usage data, including your IP address, browser
                  type and version, operating system, referring URL, pages
                  visited, and time spent on pages;
                </li>
                <li>
                  Cookie and similar tracking technology data, as further
                  described in Section 9 of this Policy; and
                </li>
                <li>
                  Information you voluntarily provide when you contact us
                  through the "Contact Us" Form or other communication channels
                  on the website, such as your name, email address, and the
                  content of your inquiry.
                </li>
              </ul>

              <h4>3.2 Information Collected from Direct Sellers</h4>
              <p>
                When you register as an Independent Direct Seller with
                IndieKonnect, and throughout your tenure as a Direct Seller, we
                collect the following categories of personal data:
              </p>
              <ul>
                <li>
                  <strong>Identity Information:</strong> Full name, date of
                  birth, gender, nationality, and photograph (where required);
                </li>
                <li>
                  <strong>Contact Information:</strong> Residential address,
                  email address, telephone number, and mobile number;
                </li>
                <li>
                  <strong>Government-issued Identification:</strong> PAN card
                  number, Aadhaar number (where applicable), or other national
                  identification, as required by applicable law and for tax
                  compliance;
                </li>
                <li>
                  <strong>Financial Information:</strong> Bank account details,
                  including account number and IFSC code, and other payment
                  information necessary to process commissions, bonuses, and
                  refunds;
                </li>
                <li>
                  <strong>Business Information:</strong> Your unique Direct
                  Seller identification number, referral network details
                  (including the identity of your sponsoring Direct Seller and
                  your downline network), business performance data under the
                  IndieKonnect Sales and Marketing Plan, qualification and rank
                  history, and purchase and order history;
                </li>
                <li>
                  <strong>Login Credentials:</strong> username and password for
                  your Direct Seller account on the website; and
                </li>
                <li>
                  <strong>Communication History:</strong> Records of
                  communications between you and IndieKonnect or between you and
                  your direct selling network, to the extent relevant to your
                  business with IndieKonnect.
                </li>
              </ul>

              <h4>3.3 Information Collected from Customers</h4>
              <p>
                When you purchase products from IndieKonnect or register as a
                Customer on our Website, we collect the following categories of
                personal data:
              </p>
              <ul>
                <li>Identity information: name and date of birth;</li>
                <li>
                  Contact information: billing address, shipping address, email
                  address, and telephone number;
                </li>
                <li>
                  Payment information: credit card, debit card, or other payment
                  method details (processed securely through our payment service
                  providers);
                </li>
                <li>
                  Order and purchase history: details of products ordered,
                  transaction amounts, and dates; and
                </li>
                <li>
                  Login credentials: username and password for your customer
                  account (if registered).
                </li>
              </ul>

              <h4>3.4 Sensitive Personal Data or Information (SPDI)</h4>
              <p>
                In accordance with the SPDI Rules, certain categories of
                information we collect including financial information such as
                bank account details and credit/debit card numbers, and
                government identification numbers, are classified as Sensitive
                Personal Data or Information. We collect, process, and retain
                SPDI only to the extent required for a lawful purpose connected
                with our direct selling business functions and activities, and
                only after obtaining your free, specific, and informed consent,
                as required under the SPDI Rules.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>4. How We Use Your Personal Data</h3>

              <h4>4.1 For Website Visitors</h4>
              <p>
                We use information collected from Website visitors for the
                following purposes:
              </p>
              <ul>
                <li>
                  To operate, maintain, and improve our website and digital
                  services;
                </li>
                <li>
                  To respond to your inquiries or requests submitted through our
                  Website;
                </li>
                <li>
                  To monitor and analyse website usage patterns for internal
                  analytics and performance improvement;
                </li>
                <li>
                  To ensure the security and integrity of our Website and
                  prevent fraudulent or unauthorised activity, and
                </li>
                <li>To comply with applicable legal obligations.</li>
              </ul>

              <h4>4.2 For Direct Sellers</h4>
              <p>
                We use personal data collected from Direct Sellers for the
                following purposes:
              </p>
              <ul>
                <li>
                  To process and manage your registration as an Independent
                  Direct Seller and maintain your account;
                </li>
                <li>
                  To administer and execute the IndieKonnect Sales and Marketing
                  Plan, including the calculation and disbursement of
                  commissions, bonuses, and incentives;
                </li>
                <li>
                  To facilitate communication between you, IndieKonnect, and
                  other members of your direct selling network, including your
                  upline and downline Direct Sellers, to the extent necessary
                  for your direct selling business;
                </li>
                <li>
                  To process your product orders and manage your purchase
                  history;
                </li>
                <li>
                  To provide you with training materials, business support, and
                  resources related to your direct selling activities;
                </li>
                <li>
                  To assess your business performance and qualifications under
                  the IndieKonnect Sales and Marketing Plan;
                </li>
                <li>
                  To conduct accounting, billing, auditing, and collections
                  activities relating to your direct selling account;
                </li>
                <li>
                  To enforce the IndieKonnect Direct Seller Agreement, Code of
                  Ethics, and applicable Rules of Conduct;
                </li>
                <li>
                  To comply with our tax reporting obligations, anti-money
                  laundering requirements, and other applicable legal and
                  regulatory requirements, and
                </li>
                <li>
                  To improve our products, services, and direct selling support
                  offerings through analytics conducted on de-identified or
                  aggregated data.
                </li>
              </ul>

              <h4>4.3 For Customers:</h4>
              <p>
                We use personal data collected from Customers for the following
                purposes:
              </p>
              <ul>
                <li>
                  To process and fulfill your product orders, including
                  arranging delivery to your specified address;
                </li>
                <li>
                  To manage your Customer account and provide related support;
                </li>
                <li>
                  To process payments and issue refunds or replacements where
                  applicable under our return and warranty policies;
                </li>
                <li>
                  To connect you with your IndieKonnect Direct Seller to
                  facilitate after-sales support and product inquiries;
                </li>
                <li>
                  To communicate with you regarding your orders, account, and
                  relevant product information;
                </li>
                <li>
                  To comply with applicable legal and regulatory obligations;
                  and
                </li>
                <li>
                  To improve our products and customer service through analytics
                  conducted on de-identified or aggregated data.
                </li>
              </ul>
            </div>

            <div class="policy-block reveal">
              <h3>5. Legal Basis for Processing:</h3>
              <p>We process your personal data on the following legal bases:</p>
              <ul>
                <li>
                  <strong>Contractual Necessity:</strong> Processing is
                  necessary for the performance of our agreement with you as a
                  Direct Seller or Customer, including fulfilling orders,
                  administering your account, and providing direct selling
                  support;
                </li>
                <li>
                  <strong>Consent:</strong> For the processing of Sensitive
                  Personal Data or Information, and for certain marketing
                  communications, we rely on your free, specific, and informed
                  written consent, which you may withdraw at any time without
                  affecting the lawfulness of processing carried out prior to
                  withdrawal;
                </li>
                <li>
                  <strong>Legal Obligation:</strong> Processing is necessary to
                  comply with applicable Indian laws, including tax laws,
                  consumer protection laws, anti-money laundering requirements,
                  and direct selling regulations; and
                </li>
                <li>
                  <strong>Legitimate Interests:</strong> Processing is necessary
                  for our legitimate business interests, including fraud
                  prevention, network security, and improving our direct selling
                  operations, where such interests are balanced against your
                  rights and interests.
                </li>
              </ul>
            </div>

            <div class="policy-block reveal">
              <h3>6. How We Share Your Personal Data:</h3>
              <p>
                <strong>IndieKonnect</strong> does not sell, rent, or trade your
                personal data to third parties for their own marketing purposes.
                We share your personal data only in the following circumstances
                and with the following categories of recipients:
              </p>

              <h4>6.1 Within the Direct Selling Network</h4>
              <p>
                As a part of the operation of our Direct Selling Model, certain
                personal data may be shared within the IndieKonnect Direct
                Seller network:
              </p>
              <ul>
                <li>
                  Your contact information and business performance data may be
                  shared with your upline Direct Sellers to enable them to
                  support and manage their direct selling network in accordance
                  with the IndieKonnect Sales and Marketing Plan;
                </li>
                <li>
                  Your contact information may be shared with your downline
                  Direct Sellers to facilitate business collaboration within
                  your network; and
                </li>
                <li>
                  Customer personal data may be shared with the Customer's
                  Direct Seller, to the extent necessary to facilitate order
                  fulfillment, customer support, and after-sales service.
                </li>
              </ul>

              <h4>6.2 Service Providers and Business Partners</h4>
              <p>
                We engage third-party service providers to support our business
                operations. These providers process your personal data only on
                our behalf and under our instructions, and are contractually
                obligated to maintain appropriate data security standards.
                Categories of service providers include:
              </p>
              <ul>
                <li>
                  Information Technology Providers, including Cloud Hosting,
                  Data Storage, and Software Development Services;
                </li>
                <li>
                  Payment Processing and Financial Transaction Service
                  Providers;
                </li>
                <li>Logistics and Delivery Service Providers;</li>
                <li>
                  Marketing, Communications, and Customer Relationship
                  Management Services; and
                </li>
                <li>Legal, Accounting, Audit, and Compliance Services.</li>
              </ul>

              <h4>6.3 Regulatory and Legal Authorities</h4>
              <p>
                We may disclose your personal data to Government Authorities,
                Regulatory Bodies, Law Enforcement Agencies, or Courts where we
                are required to do so by applicable law, court order, or legal
                process, or where we determine in good faith that such
                disclosure is reasonably necessary to protect the rights,
                property, or safety of IndieKonnect, our Direct Sellers,
                Customers, or others.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>7. How We Protect Your Personal Data</h3>
              <p>
                IndieKonnect maintains reasonable technical and organisational
                security measures designed to protect the personal data we hold
                against unauthorised access, disclosure, alteration, accidental
                loss, or destruction. Our security measures include:
              </p>
              <ul>
                <li>
                  Encryption of sensitive data in transit using
                  industry-standard protocols (SSL/TLS);
                </li>
                <li>
                  Access controls and authentication requirements to limit
                  access to personal data to authorised personnel on a
                  need-to-know basis;
                </li>
                <li>
                  Regular security assessments and vulnerability reviews of our
                  information systems;
                </li>
                <li>
                  Staff training on data protection and privacy obligations; and
                </li>
                <li>
                  Secure data disposal procedures for personal data that is no
                  longer required.
                </li>
              </ul>
              <p>
                In the event of a data breach that is likely to result in a risk
                to your rights and interests, we will take prompt action to
                contain and remediate the breach and will notify affected
                individuals and relevant regulatory authorities as required
                under applicable law.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>8. How Long We Retain Your Personal Data</h3>
              <p>
                We retain your personal data only for as long as is necessary to
                fulfil the purposes for which it was collected, as described in
                this Policy, or as required or permitted by applicable law.
                Retention periods vary depending on the category of data and the
                applicable legal requirements:
              </p>
              <ul>
                <li>
                  Direct Seller account data is retained for the duration of
                  your tenure as an active Direct Seller, and for a minimum of
                  eight (8) years thereafter for tax and legal compliance
                  purposes, unless a longer retention period is required by
                  applicable law;
                </li>
                <li>
                  Customer order and transaction data is retained for a minimum
                  of seven (7) years for accounting and legal compliance
                  purposes;
                </li>
                <li>
                  Website visitor technical and usage data is retained for a
                  period of up to twelve (12) months from collection; and
                </li>
                <li>
                  Communications data is retained for the period necessary to
                  address the relevant inquiry or matter, and for a reasonable
                  period thereafter.
                </li>
              </ul>
              <p>
                When personal data is no longer required for these purposes, we
                will securely delete or anonymise it in accordance with our data
                retention and disposal procedures.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>9. Cookies and Tracking Technologies</h3>
              <p>
                Our Website uses cookies and similar tracking technologies to
                enhance your browsing experience, analyse Website usage, and
                improve our services. A cookie is a small data file placed on
                your device when you access a Website.
              </p>
              <p>We use the following categories of cookies on our Website:</p>
              <ul>
                <li>
                  Strictly necessary cookies: These cookies are essential for
                  the operation of our Website and cannot be disabled. They
                  enable core functionality such as user authentication and
                  security.
                </li>
                <li>
                  Performance and analytics cookies: These cookies collect
                  information about how Visitors use our Website, such as which
                  pages are visited most frequently. This data is used in
                  aggregate form to improve our website and is not linked to
                  your identity.
                </li>
                <li>
                  Functionality cookies: These cookies allow our website to
                  remember your preferences and settings to provide a more
                  personalised experience.
                </li>
              </ul>
              <p>
                You may manage or disable cookies through your browser settings.
                Please note that disabling certain cookies may affect the
                functionality of our website. For further information about
                cookies and how to manage them, you may visit
                <em>www.allaboutcookies.org</em>.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>10. Third-Party Websites and Links</h3>
              <p>
                Our website may contain links to third-party websites,
                applications, or resources that are not operated or controlled
                by IndieKonnect. This Policy does not apply to those third-party
                websites, and we are not responsible for their privacy
                practices. We encourage you to review the privacy policies of
                any third-party websites you visit before submitting any
                personal data.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>11. Your Rights with Respect to Your Personal Data</h3>
              <p>
                Subject to applicable Indian Law, including the SPDI Rules and,
                for the time being in force, the Digital Personal Data
                Protection Act, 2023, you have the following rights in relation
                to your personal data held by IndieKonnect.
              </p>

              <h4>11.1 Right to Access</h4>
              <p>
                You have the right to request confirmation of whether we are
                processing your personal data, and to request access to the
                personal data we hold about you, including a copy of such data.
              </p>

              <h4>11.2 Right to Correction</h4>
              <p>
                You have the right to request that we correct inaccurate or
                incomplete personal data we hold about you. We will use all
                reasonable efforts to correct your personal data within 30
                working days of receiving a valid request.
              </p>

              <h4>11.3 Right to Withdrawal of Consent</h4>
              <p>
                Where our processing of your personal data is based on your
                consent, you have the right to withdraw your consent at any
                time. Withdrawal of consent will not affect the lawfulness of
                any processing carried out before withdrawal. Please note that
                withdrawal of consent may affect our ability to provide certain
                services to you.
              </p>

              <h4>11.4 Right to Grievance Redressal</h4>
              <p>
                You have the right to have any grievances relating to the
                processing of your personal data addressed by our Grievance
                Officer, whose contact details are provided in Section 13 of
                this Policy. We will endeavour to acknowledge and respond to all
                grievances within 30 working days of receipt.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>12. Updates to This Privacy Policy</h3>
              <p>
                IndieKonnect may update this Privacy Policy from time to time to
                reflect changes in our business practices, applicable laws, or
                regulatory requirements. We will notify you of any material
                changes to this Policy by:
              </p>
              <ul>
                <li>
                  Posting the updated Policy on our Website with a revised
                  effective date;
                </li>
                <li>
                  Sending a notification to your registered email address, or
                </li>
                <li>
                  Publishing a notice through official IndieKonnect
                  communications channels.
                </li>
              </ul>
              <p>
                All changes will become effective from the date of publication
                unless otherwise specified. Your continued use of our website or
                services after the effective date of any update constitutes your
                acceptance of the updated Policy. We encourage you to review
                this Policy periodically.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>13. Grievance Officer and Contact Information:</h3>
              <p>
                In accordance with the Information Technology Act, 2000 and the
                SPDI Rules, IndieKonnect has designated a Grievance Officer to
                address complaints and concerns relating to the processing of
                personal data. If you have any questions, concerns, or
                grievances regarding this Privacy Policy or our data practices,
                please contact our Grievance Officer:
              </p>
              <p>
                <strong>Grievance Officer</strong><br />
                IndieKonnect<br />
                Email:
                <a href="mailto:customercare@indiekonnect.com"
                  >customercare@indiekonnect.com</a
                >
              </p>
              <p>
                We will use reasonable efforts to acknowledge your complaint
                within 48 hrs of receipt and to resolve the matter within 30
                working days. If you are not satisfied with our response, you
                may escalate the matter to the appropriate regulatory authority.
              </p>
            </div>

            <div class="policy-block reveal">
              <h3>14. Compliance with Direct Selling Regulations</h3>
              <p>
                IndieKonnect is committed to full compliance with the Consumer
                Protection (Direct Selling) Rules, 2021 and all other applicable
                regulations governing direct selling businesses in India. Our
                processing of personal data is conducted in strict accordance
                with these legal requirements, including the obligations
                relating to the transparency of our direct selling compensation
                plan, the rights of Direct Sellers and Customers, and the
                prohibition of unfair trade practices.
              </p>
              <p>
                Direct Sellers and Customers who have concerns about our
                compliance with direct selling regulations may raise their
                concerns with our Grievance Officer or contact the relevant
                Consumer Protection authority.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
    <FooterView />
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import AppNavbar from "../AppNavbar.vue";
import FooterView from "../FooterView.vue";

onMounted(() => {
  const body = document.body;
  if (body) {
    body.style.setProperty("margin", "0", "important");
    body.style.setProperty("padding", "0", "important");
    body.style.setProperty("max-width", "100%", "important");
  }

  const revealElements = document.querySelectorAll(".reveal");
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    },
    { threshold: 0.08 },
  );
  revealElements.forEach((el) => observer.observe(el));
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800&display=swap");

* {
  box-sizing: border-box;
}

.privacy-page-root {
  width: 100vw !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  font-family: "Raleway", sans-serif;
  background: #f7f9fc;
  color: #333f48;
  overflow-x: hidden;
}

.hero-full-bleed-row {
  width: 100% !important;
  background: #003da5 !important;
  display: block;
}

.privacy-hero {
  width: 100% !important;
  max-width: 1240px;
  margin: 0 auto;
  padding: 30px 24px 20px;
}

.hero-content {
  width: 100%;
  max-width: 820px;
}

.eyebrow {
  color: #ffc72c;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 5px;
}

.privacy-hero h1 {
  color: white;
  font-size: 54px;
  line-height: 1;
  margin-bottom: 18px;
  font-weight: 800;
}

.hero-description {
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  line-height: 1.7;
  max-width: 650px;
}

.content-full-bleed-row {
  width: 100% !important;
  background: #f7f9fc;
  display: block;
}

.policy-section {
  width: 100% !important;
  padding: 20px 0 60px;
}

.policy-wrapper {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 24px;
}

.policy-block {
  margin-bottom: 42px;
}

.policy-block h2 {
  font-size: 34px;
  color: #003da5;
  margin-bottom: 18px;
  font-weight: 800;
}

.policy-block h3 {
  font-size: 24px;
  color: #003da5;
  margin-bottom: 14px;
  font-weight: 800;
}

.policy-block h4 {
  font-size: 17px;
  color: #003da5;
  margin-bottom: 10px;
  margin-top: 22px;
  font-weight: 700;
}

.policy-block p {
  font-size: 15px;
  line-height: 1.9;
  color: #4c5661;
  margin-bottom: 14px;
}

.policy-block p a {
  color: #003da5;
  text-decoration: underline;
}

.policy-block ul {
  margin: 0 0 16px 0;
  padding-left: 22px;
}

.policy-block ul li {
  font-size: 15px;
  line-height: 1.9;
  color: #4c5661;
  margin-bottom: 6px;
  list-style-type: disc;
}

.reveal {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 768px) {
  .privacy-hero {
    padding: 40px 20px 20px;
  }

  .privacy-hero h1 {
    font-size: 26px;
  }

  .policy-section {
    padding: 20px 0 40px;
  }

  .policy-wrapper {
    padding: 0 20px;
  }

  .policy-block h2 {
    font-size: 28px;
  }

  .policy-block h3 {
    font-size: 21px;
  }

  .policy-block h4 {
    font-size: 15px;
  }
}
</style>
